<script setup lang="ts">
  // Ported from cartadigital-pos app/components/pos/order/PosOrderCart.vue.
  // Online-first: no caja gate, no offline claims. Persist goes through the
  // drafts store (v2Create/v2Update) with PIN escalation mirroring the backend.
  import { computed, getCurrentInstance, ref } from 'vue';
  import type { MaybeElement } from '@vueuse/core';
  import { OrderType } from '@/enums/OrderEnums';
  import { AlertType } from '@/interfaces/IAlert';
  import { IAuthData } from '@/interfaces/IAuthData';
  import { PermissionType } from '@/enums/PermissionType';
  import { PosV2Error } from '@/domain/pos/pos-error';
  import type { IPosCartLine, IPosDraft } from '@/domain/pos/pos-types';
  import {
    formatCartItemOptions,
    formatCartItemOptionsSimple,
    getOrderHumanTitle,
  } from '@/domain/pos/order-formatting';
  import { adaptItemOptions } from '@/domain/pos/option-adapter';
  import { useAuth } from '@/composition/use-auth';
  import { useCartCompaction } from '@/composition/use-cart-compaction';
  import { useMenuStore } from '@/composition/use-menu';
  import { useModal } from '@/composition/use-modal';
  import { useOrders } from '@/composition/use-orders';
  import { usePosMoney } from '@/composition/use-pos-money';
  import { useUi } from '@/composition/use-ui';
  import { useOrdersStore } from '@/stores/orders.store';
  import { usePosDraftsStore } from '@/stores/pos-drafts.store';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { useReservationsUpcoming } from '@/composition/reservations/use-reservations-upcoming';
  import { getTableReservations } from '@/domain/reservations/pos-table-hints';
  import ModalAdminPin from '@/components/modals/ModalAdminPin.vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiPopover from '@/components/pos-v2/ui/PosUiPopover.vue';
  import PosUiScrollable from '@/components/pos-v2/ui/PosUiScrollable.vue';
  import PosV2CoversModal, {
    IPosV2CoversResult,
  } from '@/components/pos-v2/modals/PosV2CoversModal.vue';
  import PosV2CartTotals from './PosV2CartTotals.vue';
  import PosV2CustomFields from './PosV2CustomFields.vue';
  import PosV2OrderComment from './PosV2OrderComment.vue';

  const emit = defineEmits<{
    (e: 'edit-line', line: IPosCartLine): void;
  }>();

  const posDrafts = usePosDraftsStore();
  const restaurantStore = useRestaurantStore();
  const menuStore = useMenuStore();
  const money = usePosMoney();
  const ui = useUi();
  const modals = useModal();
  const auth = useAuth();
  const orders = useOrders();
  const ordersStore = useOrdersStore();
  const upcoming = useReservationsUpcoming();

  // vue-i18n in script (Vue 2.7): translate through the component proxy.
  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  const draft = computed(() => posDrafts.activeDraft);
  const cartLines = computed(() => draft.value?.cartLines ?? []);
  const totals = computed(() => draft.value?.totals ?? null);

  const isEditMode = computed(
    () => !!draft.value?.orderId && draft.value?.mode === 'edit'
  );

  const persisting = ref(false);

  const editConflict = computed(() => posDrafts.activeDraftEditConflict);

  const validationError = computed(() => {
    if (!draft.value) return null;
    return posDrafts.getValidationError(draft.value);
  });

  const primaryDisabled = computed(() => {
    if (!draft.value || persisting.value) return true;
    if (validationError.value !== null) return true;
    if (isEditMode.value) return !draft.value.isDirty || editConflict.value;
    return false;
  });

  //#region Compaction (short viewports)

  const activeFields = computed(() =>
    draft.value ? posDrafts.getActiveCustomFields(draft.value.type) : []
  );

  // Template refs for the measurement-driven tier engine. They always point
  // at the inline footer instances, never the popover-hosted copies.
  const linesRef = ref<MaybeElement>(null);
  const fieldsRef = ref<MaybeElement>(null);
  const commentRef = ref<MaybeElement>(null);
  const totalsRef = ref<MaybeElement>(null);
  const chipsRowRef = ref<MaybeElement>(null);
  const errorRef = ref<MaybeElement>(null);
  const conflictRef = ref<MaybeElement>(null);

  const { tier, isTiny, commentRows } = useCartCompaction({
    linesRef,
    fieldsRef,
    commentRef,
    totalsRef,
    chipsRowRef,
    errorRef,
    conflictRef,
    activeFields,
    totals,
  });

  const fieldsFilledCount = computed(() => {
    const values = draft.value?.customFieldValues ?? {};
    return activeFields.value.filter((f) => (values[String(f.id)] ?? '').trim())
      .length;
  });

  // Same rule as getValidationError's mandatory-field check.
  const hasMissingMandatoryField = computed(() => {
    const values = draft.value?.customFieldValues ?? {};
    return activeFields.value.some(
      (f) => f.mandatory && !(values[String(f.id)] ?? '').trim()
    );
  });

  const commentFilled = computed(
    () => !!(draft.value?.userComment ?? '').trim()
  );

  //#endregion

  //#region Lines

  function getItemName(itemId: number): string {
    return (
      menuStore.itemMap?.[itemId]?.name ??
      i18nT('posV2.productFallback', { id: itemId })
    );
  }

  function getOptionsLabel(line: IPosCartLine): string {
    if (!line.optionValues || Object.keys(line.optionValues).length === 0) {
      return '';
    }

    const item = menuStore.itemMap?.[line.itemId];
    if (item) {
      return formatCartItemOptions(line.optionValues, adaptItemOptions(item));
    }
    return formatCartItemOptionsSimple(line.optionValues);
  }

  async function handleRemoveLine(line: IPosCartLine) {
    const currentDraft = draft.value;
    if (!currentDraft) return;
    const draftId = currentDraft.id;

    const confirmed = await modals.confirm({
      title: i18nT('posV2.removeItemTitle'),
      message: i18nT('posV2.removeItemConfirm', {
        item: getItemName(line.itemId),
      }),
      acceptText: i18nT('posV2.remove'),
      acceptColor: 'error',
    });
    if (!confirmed) return;

    posDrafts.removeCartLine(draftId, line.index);
  }

  async function handleClearCart() {
    const currentDraft = draft.value;
    if (!currentDraft || currentDraft.cartLines.length === 0) return;
    const draftId = currentDraft.id;

    const confirmed = await modals.confirm({
      title: i18nT('posV2.clearCartTitle'),
      message: i18nT('posV2.clearCartConfirm', {
        count: currentDraft.cartLines.length,
      }),
      acceptText: i18nT('posV2.clear'),
      acceptColor: 'error',
    });
    if (!confirmed) return;

    posDrafts.clearCart(draftId);
  }

  async function handleUpdateQuantity(line: IPosCartLine, delta: number) {
    if (!draft.value) return;

    const newQuantity = line.quantity + delta;
    if (newQuantity < 1) {
      await handleRemoveLine(line);
      return;
    }

    posDrafts.setQuantity(draft.value.id, line.index, newQuantity);
  }

  //#endregion

  //#region Meta

  // Takes the draft explicitly: callers run after awaits, during which the
  // user may have switched tabs (draft.value would then be the wrong draft).
  function getDraftTitle(d: IPosDraft): string {
    const order = d.orderId
      ? ordersStore.orders?.find((o) => o.id === d.orderId) ?? null
      : null;

    const table = d.tableId
      ? restaurantStore.tables?.find((t) => t.id === d.tableId)
      : null;

    return getOrderHumanTitle({
      type: d.type,
      humanId: order?.humanId ?? null,
      tableName: table?.name ?? null,
      customerName: d.customerData.name || null,
      customerPhone: d.customerData.phone || null,
      deliveryAddress: d.deliveryInfo?.address ?? null,
      deliveryAddress2: d.deliveryInfo?.address2 ?? null,
    });
  }

  //#endregion

  //#region Persist

  function toastPosError(error: unknown, fallback: string) {
    if (error instanceof PosV2Error) {
      ui.showToast(AlertType.Error, String(i18nT(error.i18nKey, error.params)));
      return;
    }
    ui.serverError(error, fallback);
  }

  /**
   * Whether the target Seat table is idle (no orders or seated reservation): creating
   * on it "opens" the table, so we ask for covers first (informative only).
   */
  function isSeatTableIdle(tableId: number): boolean {
    const pendingSeat = ordersStore.pendingOrdersByType?.[OrderType.Seat] ?? [];
    return (
      !pendingSeat.some((o) => o.tableId === tableId) &&
      !getTableReservations(upcoming.list.value ?? [], tableId).some(
        (reservation) => reservation.status === 'seated'
      )
    );
  }

  async function handleCreate() {
    const currentDraft = draft.value;
    if (!currentDraft || persisting.value) return;
    const draftId = currentDraft.id;

    persisting.value = true;
    try {
      // Covers prompt when this order opens the table. Branch policy:
      // 'off' skips the prompt, 'required' hides the skip button.
      const coversMode =
        restaurantStore.selectedBranch?.coversPromptMode ?? 'optional';
      if (
        coversMode !== 'off' &&
        currentDraft.type === OrderType.Seat &&
        typeof currentDraft.tableId === 'number' &&
        currentDraft.covers == null &&
        isSeatTableIdle(currentDraft.tableId)
      ) {
        const table = restaurantStore.tables?.find(
          (t) => t.id === currentDraft.tableId
        );
        const result = await modals.showDynamicDialog<IPosV2CoversResult | null>(
          PosV2CoversModal,
          {
            tableName: table?.name ?? null,
            initial: currentDraft.covers,
            required: coversMode === 'required',
          },
          'pos-covers',
          // 'required' must not be bypassable via backdrop/ESC dismissal.
          { width: 420, clickToClose: coversMode !== 'required' }
        );

        // While the modal was open the tab may have been closed, or bound to
        // an order that landed late (it is no longer a create draft).
        const live = posDrafts.drafts.find((d) => d.id === draftId);
        if (!live || live.mode !== 'create') return;
        if (result) {
          posDrafts.updateDraft(draftId, { covers: result.covers });
        }
      }

      // The store converts the tab (view / edit / split) and toasts.
      await posDrafts.persistCreate(draftId);
    } catch (error) {
      toastPosError(error, i18nT('posV2.createOrderError'));
    } finally {
      persisting.value = false;
    }
  }

  async function handleSave() {
    const currentDraft = draft.value;
    if (!currentDraft?.orderId || persisting.value) return;
    const draftId = currentDraft.id;

    // Belt-and-braces with primaryDisabled: never save over a stale baseline.
    if (editConflict.value) {
      ui.showToast(AlertType.Error, i18nT('posV2.orderChangedBanner'));
      return;
    }

    persisting.value = true;
    try {
      // Backend escalation mirror: removing/decreasing persisted lines
      // requires OrdersCancel — collect PIN upfront when the user lacks it.
      let authData: IAuthData | undefined;
      const needsCancel = await posDrafts.updateNeedsCancelPermission(draftId);
      if (needsCancel && !auth.hasPermission(PermissionType.OrdersCancel)) {
        authData = await modals.showDynamicDialog<IAuthData>(
          ModalAdminPin,
          {
            requiredPermission: PermissionType.OrdersCancel,
            reason: 'order-update-remove-lines',
          },
          'admin-pin'
        );
        if (!authData) return;
      }

      if (!posDrafts.drafts.find((d) => d.id === draftId)) return;

      const order = await posDrafts.persistUpdate(draftId, authData);

      const title = getDraftTitle(currentDraft);

      // Back to the read-only view of the order (parity with -pos).
      if (posDrafts.drafts.find((d) => d.id === draftId)) {
        posDrafts.loadFromOrder(draftId, order);
        posDrafts.setMode(draftId, 'view');
      }

      ui.showToast(AlertType.Success, i18nT('posV2.updatedToast', { title }));
    } catch (error) {
      toastPosError(error, i18nT('posV2.saveOrderError'));
    } finally {
      persisting.value = false;
    }
  }

  async function handleSubmit() {
    if (!draft.value) return;

    if (validationError.value) {
      ui.showToast(
        AlertType.Error,
        String(i18nT(validationError.value.key, validationError.value.params))
      );
      return;
    }

    if (isEditMode.value) {
      await handleSave();
    } else {
      await handleCreate();
    }
  }

  async function handleRefreshBaseline() {
    const currentDraft = draft.value;
    if (!currentDraft || persisting.value) return;
    const draftId = currentDraft.id;

    if (currentDraft.isDirty) {
      const confirmed = await modals.confirm({
        title: i18nT('posV2.discardChangesTitle'),
        message: i18nT('posV2.discardChangesConfirm'),
        acceptText: i18nT('posV2.discard'),
        acceptColor: 'warning',
      });
      if (!confirmed) return;
    }

    try {
      await posDrafts.refreshEditBaseline(draftId);
    } catch (error) {
      toastPosError(error, i18nT('posV2.orderOpenError'));
    }
  }

  async function handleCancelEdit() {
    const currentDraft = draft.value;
    if (!currentDraft || !isEditMode.value) return;
    const draftId = currentDraft.id;
    const orderId = currentDraft.orderId;
    const wasDirty = currentDraft.isDirty;

    if (wasDirty) {
      const confirmed = await modals.confirm({
        title: i18nT('posV2.discardChangesTitle'),
        message: i18nT('posV2.discardChangesConfirm'),
        acceptText: i18nT('posV2.discard'),
        acceptColor: 'warning',
      });
      if (!confirmed) return;
    }

    // Return to the read-only view of the order (parity with -pos); the tab
    // only closes if the order can't be resolved anymore.
    if (orderId) {
      try {
        const order =
          ordersStore.orders?.find((o) => o.id === orderId) ??
          (await orders.refreshOrder({
            restaurantId: restaurantStore.restaurant!.id,
            orderId,
          }));

        if (!posDrafts.drafts.find((d) => d.id === draftId)) return;

        posDrafts.loadFromOrder(draftId, order);
        posDrafts.setMode(draftId, 'view');
        if (wasDirty) {
          ui.showToast(AlertType.Info, i18nT('posV2.changesDiscarded'));
        }
        return;
      } catch (e) {
        console.error('Error reverting edit to view', e);
      }
    }

    posDrafts.closeDraft(draftId, true);
    if (wasDirty) {
      ui.showToast(AlertType.Info, i18nT('posV2.changesDiscarded'));
    }
  }

  //#endregion
</script>

<template>
  <div class="pos-v2-cart" :class="{ 'is-tiny': isTiny }">
    <!-- Fixed header -->
    <div class="cart-header">
      <h2 class="cart-title">{{ $t('posV2.cart') }}</h2>
      <PosUiButton
        v-if="cartLines.length > 0"
        color="error"
        variant="ghost"
        size="sm"
        icon="trash-can-outline"
        @click="handleClearCart"
      >
        {{ $t('posV2.clear') }}
      </PosUiButton>
    </div>

    <!-- Scrollable lines -->
    <PosUiScrollable
      ref="linesRef"
      class="cart-body-container"
      orientation="vertical"
      variant="pseudo"
      theme="elevated"
      :offset-top="10"
      :offset-bottom="10"
      size="md"
      scroll-class="cart-body"
    >
      <div v-if="cartLines.length === 0" class="cart-empty">
        <PosUiIcon name="cart-outline" class="empty-icon" />
        <p class="empty-text">{{ $t('posV2.cartEmpty') }}</p>
      </div>

      <div v-else class="cart-lines">
        <div
          v-for="line in cartLines"
          :key="line.index"
          class="cart-line"
          @click="emit('edit-line', line)"
        >
          <div class="line-info">
            <div class="line-name">
              <span class="line-qty-indicator">{{ line.quantity }}x</span>
              {{ getItemName(line.itemId) }}
            </div>
            <div v-if="getOptionsLabel(line)" class="line-options">
              {{ getOptionsLabel(line) }}
            </div>
            <div v-if="line.userComment" class="line-comment">
              <PosUiIcon name="message-outline" class="comment-icon" />
              <span class="comment-text">{{ line.userComment }}</span>
            </div>
          </div>

          <div class="line-controls">
            <div class="line-stepper">
              <PosUiButton
                icon="minus"
                color="neutral"
                variant="soft"
                size="xs"
                @click.stop="handleUpdateQuantity(line, -1)"
              />
              <span class="line-quantity">{{ line.quantity }}</span>
              <PosUiButton
                icon="plus"
                color="neutral"
                variant="soft"
                size="xs"
                @click.stop="handleUpdateQuantity(line, 1)"
              />
            </div>
            <div class="line-price">
              <span class="unit-price">
                {{ money.formatComputedMoney(line.unitPrice) }}
              </span>
              <span class="total-price">
                {{ money.formatComputedMoney(line.totalPrice) }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </PosUiScrollable>

    <!-- Fixed footer -->
    <div v-if="draft" class="cart-footer" :class="{ 'is-compact': tier >= 1 }">
      <!-- Gated on activeFields here (not only inside the child): with zero
           fields the child's root v-if would leave a comment node as $el,
           which ResizeObserver.observe() rejects. -->
      <PosV2CustomFields
        v-if="tier < 2 && activeFields.length > 0"
        ref="fieldsRef"
      />

      <PosV2OrderComment v-if="tier < 2" ref="commentRef" :rows="commentRows" />

      <!-- Collapsed chips (tier >= 1). Panels are parent-anchored so they
           span the footer's full width instead of clipping against the
           narrow overflow-hidden cart column. -->
      <div v-if="tier >= 1" ref="chipsRowRef" class="cart-chips">
        <PosUiPopover
          v-if="tier === 2 && activeFields.length > 0"
          anchor="parent"
          placement="top"
        >
          <template #trigger="{ toggle }">
            <button
              type="button"
              class="cart-chip"
              :class="{
                'is-error': hasMissingMandatoryField,
                'is-filled': !hasMissingMandatoryField && fieldsFilledCount > 0,
              }"
              @click="toggle"
            >
              <span v-if="hasMissingMandatoryField" class="chip-dot" />
              <PosUiIcon
                v-else-if="fieldsFilledCount > 0"
                name="check"
                class="chip-check"
              />
              <span class="chip-label">
                {{ $t('posV2.chipData')
                }}<template v-if="hasMissingMandatoryField">*</template>
              </span>
              <span class="chip-count">
                {{ fieldsFilledCount }}/{{ activeFields.length }}
              </span>
            </button>
          </template>
          <PosV2CustomFields />
        </PosUiPopover>

        <PosUiPopover v-if="tier === 2" anchor="parent" placement="top">
          <template #trigger="{ toggle }">
            <button
              type="button"
              class="cart-chip"
              :class="{ 'is-filled': commentFilled }"
              @click="toggle"
            >
              <PosUiIcon v-if="commentFilled" name="check" class="chip-check" />
              <span class="chip-label">{{ $t('posV2.chipComment') }}</span>
            </button>
          </template>
          <PosV2OrderComment :rows="3" />
        </PosUiPopover>

        <PosUiPopover anchor="parent" placement="top">
          <template #trigger="{ toggle }">
            <button type="button" class="cart-chip" @click="toggle">
              <PosUiIcon name="sigma" class="chip-icon" />
              <span class="chip-label">{{ $t('posV2.chipDetails') }}</span>
            </button>
          </template>
          <PosV2CartTotals :totals="totals" />
        </PosUiPopover>
      </div>

      <PosV2CartTotals v-if="tier === 0" ref="totalsRef" :totals="totals" />

      <div v-if="validationError" ref="errorRef" class="validation-error">
        <PosUiIcon name="alert-circle-outline" />
        <span>{{ $t(validationError.key, validationError.params) }}</span>
      </div>

      <div
        v-if="isEditMode && editConflict && !persisting"
        ref="conflictRef"
        class="conflict-banner"
      >
        <PosUiIcon name="alert-outline" class="conflict-icon" />
        <span class="conflict-text">{{ $t('posV2.orderChangedBanner') }}</span>
        <PosUiButton
          color="warning"
          variant="soft"
          size="sm"
          icon="refresh"
          @click="handleRefreshBaseline"
        >
          {{ $t('posV2.orderChangedRefresh') }}
        </PosUiButton>
      </div>

      <div class="cart-actions">
        <PosUiButton
          v-if="isEditMode"
          color="neutral"
          variant="soft"
          size="xl"
          icon="close"
          class="cart-button"
          :class="{ 'is-icon-only': tier >= 1 }"
          :disabled="persisting"
          @click="handleCancelEdit"
        >
          <template v-if="tier < 1">{{ $t('posV2.cancel') }}</template>
        </PosUiButton>

        <PosUiButton
          color="primary"
          variant="solid"
          size="xl"
          :disabled="primaryDisabled"
          :loading="persisting"
          icon="check"
          class="cart-button"
          :class="{ 'cta-with-amount': tier >= 1 }"
          @click="handleSubmit"
        >
          <template v-if="tier >= 1">
            <span class="cta-label">
              {{ isEditMode ? $t('posV2.save') : $t('posV2.create') }}
            </span>
            <span class="cta-amount">
              {{ money.formatComputedMoney(totals?.total || 0) }}
            </span>
          </template>
          <template v-else>
            {{ isEditMode ? $t('posV2.save') : $t('posV2.create') }}
          </template>
        </PosUiButton>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .pos-v2-cart {
    /* Card chrome only on lg+: the mobile pane is full-bleed */
    @apply tw-flex tw-h-full tw-flex-col tw-rounded-none tw-border-0 tw-border-neutral-200 tw-bg-white lg:tw-rounded-lg lg:tw-border;

    .cart-header {
      @apply tw-flex tw-items-center tw-justify-between tw-border-b tw-border-neutral-200 tw-px-4 tw-py-3;

      .cart-title {
        @apply tw-text-lg tw-font-semibold tw-text-neutral-900;
      }
    }

    :deep(.cart-body-container) {
      @apply tw-relative tw-min-h-0 tw-flex-1;
    }

    :deep(.cart-body) {
      @apply tw-relative tw-flex-1 tw-overflow-y-auto;
    }

    .cart-empty {
      @apply tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-py-8 tw-text-neutral-500;

      .empty-icon {
        @apply tw-text-4xl;
      }

      .empty-text {
        @apply tw-text-base;
      }
    }

    .cart-lines {
      @apply tw-flex tw-min-h-full tw-flex-col tw-gap-1 tw-p-1.5;
    }

    .cart-line {
      @apply tw-flex tw-min-h-[40px] tw-cursor-pointer tw-flex-row tw-items-start tw-gap-2 tw-rounded-lg tw-border tw-border-neutral-200 tw-bg-white tw-px-2 tw-py-1.5 tw-transition-all tw-duration-150 hover:tw-border-neutral-300;

      .line-info {
        @apply tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-0.5;
      }

      .line-name {
        @apply tw-truncate tw-text-sm tw-font-semibold tw-text-neutral-900;

        .line-qty-indicator {
          @apply tw-text-xs tw-font-normal tw-text-neutral-500;
        }
      }

      .line-options {
        @apply tw-line-clamp-1 tw-text-xs tw-text-neutral-500;
      }

      .line-comment {
        @apply tw-flex tw-items-start tw-gap-1 tw-text-xs tw-text-sky-700;

        .comment-icon {
          @apply tw-mt-0.5 tw-flex-shrink-0 tw-text-xs;
        }

        .comment-text {
          @apply tw-line-clamp-1;
        }
      }

      .line-controls {
        @apply tw-flex tw-flex-shrink-0 tw-flex-col tw-items-end tw-gap-0.5;
      }

      .line-stepper {
        @apply tw-flex tw-items-center tw-gap-1;
      }

      .line-quantity {
        @apply tw-min-w-[1.25rem] tw-text-center tw-text-sm tw-font-semibold tw-text-neutral-900;
      }

      .line-price {
        @apply tw-flex tw-items-center tw-gap-1;

        .unit-price {
          @apply tw-text-xs tw-text-neutral-500;
        }

        .total-price {
          @apply tw-text-sm tw-font-semibold tw-tabular-nums tw-text-neutral-900;
        }
      }
    }

    .cart-footer {
      // Relative: it is the positioned ancestor the parent-anchored chip
      // popovers dock their full-width panels against.
      @apply tw-relative tw-flex tw-flex-shrink-0 tw-flex-col tw-gap-3 tw-border-t tw-border-neutral-200 tw-px-3 tw-py-3;

      &.is-compact {
        @apply tw-gap-2;
      }
    }

    .cart-chips {
      @apply tw-flex tw-flex-wrap tw-gap-1.5;

      .cart-chip {
        @apply tw-inline-flex tw-h-9 tw-items-center tw-gap-1.5 tw-rounded-lg tw-border tw-border-neutral-300 tw-bg-white tw-px-3 tw-text-xs tw-font-medium tw-text-neutral-700;

        .chip-dot {
          @apply tw-h-2 tw-w-2 tw-flex-shrink-0 tw-rounded-full tw-bg-red-500;
        }

        .chip-check {
          @apply tw-flex-shrink-0 tw-text-sm tw-text-emerald-600;
        }

        .chip-icon {
          @apply tw-flex-shrink-0 tw-text-sm tw-text-neutral-500;
        }

        .chip-count {
          @apply tw-text-[10px] tw-font-semibold tw-text-neutral-500;
        }

        &.is-error {
          @apply tw-border-red-400 tw-text-red-600;

          .chip-count {
            @apply tw-text-red-500;
          }
        }

        &.is-filled {
          @apply tw-border-emerald-300 tw-bg-emerald-50 tw-text-emerald-700;

          .chip-count {
            @apply tw-text-emerald-600;
          }
        }
      }
    }

    .validation-error {
      @apply tw-flex tw-items-center tw-gap-1.5 tw-rounded-lg tw-bg-red-600/10 tw-px-2.5 tw-py-1.5 tw-text-xs tw-text-red-600;
    }

    .conflict-banner {
      @apply tw-flex tw-items-center tw-gap-1.5 tw-rounded-lg tw-border tw-border-amber-300 tw-bg-amber-50 tw-px-2.5 tw-py-1.5 tw-text-xs tw-text-amber-900;

      .conflict-icon {
        @apply tw-flex-shrink-0 tw-text-amber-600;
      }

      .conflict-text {
        @apply tw-flex-1;
      }
    }

    .cart-actions {
      @apply tw-flex tw-flex-col tw-gap-2 sm:tw-flex-row;

      :deep(.cart-button) {
        @apply tw-w-full md:tw-min-h-[52px];
      }

      // Edit mode at tier >= 1: Cancel is icon-only so both buttons plus
      // the embedded amount fit the 320px cart column.
      :deep(.cart-button.is-icon-only) {
        @apply sm:tw-w-auto sm:tw-flex-none sm:tw-px-5;
      }

      // PosUiButton wraps its slot in .pos-ui-button-label; stretch it so
      // the label and the amount sit at opposite ends of the CTA.
      .cta-with-amount :deep(.pos-ui-button-label) {
        @apply tw-flex-1 tw-justify-between;
      }
    }
  }

  .pos-v2-cart.is-tiny {
    // Tiny guardrail: the lines area keeps a 120px minimum and the whole
    // pane scrolls instead of clipping the CTA off-screen.
    @apply tw-overflow-y-auto;

    :deep(.cart-body-container) {
      @apply tw-min-h-[120px] tw-flex-none tw-basis-[120px] lg:tw-flex-auto;
    }
  }
</style>
